/**
 * Modal 480 px "Nuevo turno" / "Editar nombre" (008 · T044 · US1; contracts/ui.md §1.3).
 *
 * - Alta: nombre, código, inicio, fin (ayuda "Si termina antes de empezar, cruza la medianoche"),
 *   refrigerio y "Usar tolerancias propias" (checkbox que muestra dos campos). Sin tolerancias
 *   propias se envía `null` y rige la de la empresa.
 * - Edición de nombre/código (`PATCH`): solo esos dos campos; sin cambios no se llama a la API.
 *
 * La validación es la del contrato (`CreateShiftTemplateBodySchema`/`UpdateShiftTemplateBodySchema`,
 * mismos rangos y refinamientos que la API): los issues `custom` ya traen su texto es-PE; el resto
 * usa el texto por campo de `schedules.json`. Los errores de la API vuelven al campo cuando traen
 * `details.field` o `details.issues[].path`.
 *
 * `ShiftTimesFields` y `shiftTimesFromValues` los reutiliza `template-version-dialog.tsx`.
 */
import { useEffect } from 'react';
import { useForm, useWatch, type Path, type UseFormReturn } from 'react-hook-form';

import {
  CreateShiftTemplateBodySchema,
  SHIFT_MAX_DURATION_MINUTES,
  SHIFT_MIN_DURATION_MINUTES,
  shiftDurationMinutes,
  UpdateShiftTemplateBodySchema,
  type ShiftTemplate,
  type ShiftTimesBody,
  type UpdateShiftTemplateBody,
} from '@fermin/contracts';

import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import { Field } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { useToast } from '../../../components/ui/toast';
import { t } from '../../../i18n';
import { readOnlyProps, useReadOnly } from '../../shell/use-read-only';
import { scheduleErrorMessage, useCreateShiftTemplate, useUpdateShiftTemplate } from '../api';
import { durationLabel } from '../format';

import type { ApiError } from '../../../api/client';
import type { z } from 'zod';

// ---------------------------------------------------------------------------
// Campos de horario compartidos (alta y nueva versión)
// ---------------------------------------------------------------------------

/** Valores del formulario de horas: los números van como texto para distinguir "vacío". */
export interface ShiftTimesValues {
  start_time: string;
  end_time: string;
  break_minutes: string;
  own_tolerances: boolean;
  late_tolerance_minutes: string;
  early_leave_tolerance_minutes: string;
}

export const SHIFT_TIME_FIELDS = [
  'start_time',
  'end_time',
  'break_minutes',
  'late_tolerance_minutes',
  'early_leave_tolerance_minutes',
] as const;

/** Texto → número; vacío ⇒ `fallback` (NaN hace fallar la validación del contrato). */
function toNumber(value: string, fallback: number | null): number | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return fallback;
  }
  return Number(trimmed);
}

/** Arma la parte de horas del cuerpo a partir del formulario. */
export function shiftTimesFromValues(values: ShiftTimesValues): ShiftTimesBody {
  return {
    start_time: values.start_time,
    end_time: values.end_time,
    break_minutes: toNumber(values.break_minutes, 0) ?? 0,
    late_tolerance_minutes: values.own_tolerances
      ? toNumber(values.late_tolerance_minutes, Number.NaN)
      : null,
    early_leave_tolerance_minutes: values.own_tolerances
      ? toNumber(values.early_leave_tolerance_minutes, Number.NaN)
      : null,
  };
}

/** Valores iniciales de horas desde una versión existente (o vacíos). */
export function shiftTimesValues(
  version?: Pick<
    ShiftTimesBody,
    | 'start_time'
    | 'end_time'
    | 'break_minutes'
    | 'late_tolerance_minutes'
    | 'early_leave_tolerance_minutes'
  >,
): ShiftTimesValues {
  if (!version) {
    return {
      start_time: '',
      end_time: '',
      break_minutes: '0',
      own_tolerances: false,
      late_tolerance_minutes: '',
      early_leave_tolerance_minutes: '',
    };
  }
  const own =
    version.late_tolerance_minutes !== null || version.early_leave_tolerance_minutes !== null;
  return {
    start_time: version.start_time,
    end_time: version.end_time,
    break_minutes: String(version.break_minutes),
    own_tolerances: own,
    late_tolerance_minutes: own ? String(version.late_tolerance_minutes ?? 0) : '',
    early_leave_tolerance_minutes: own ? String(version.early_leave_tolerance_minutes ?? 0) : '',
  };
}

/**
 * Pone en el formulario los issues de un `safeParse` del contrato. Devuelve `true` si hubo alguno.
 * Los issues `custom` (refinamientos del contrato) ya vienen en es-PE; el resto usa el texto del
 * campo.
 */
export function applyIssues<T extends Record<string, unknown>>(
  form: UseFormReturn<T>,
  issues: readonly z.core.$ZodIssue[],
  knownFields: readonly string[],
): boolean {
  let applied = false;
  for (const issue of issues) {
    const field = String(issue.path[0] ?? '');
    if (!knownFields.includes(field)) {
      continue;
    }
    const message =
      issue.code === 'custom' ? issue.message : t(`schedules.templates.form.errors.${field}`);
    form.setError(field as Path<T>, { message });
    applied = true;
  }
  return applied;
}

/**
 * Traduce un error de la API al formulario: `CONFLICT` con `details.field` (nombre o código
 * duplicado) o `VALIDATION_ERROR` con `details.issues[].path`. Devuelve `false` si no hay campo al
 * que asignarlo (quien llama muestra un toast).
 */
export function applyApiFieldError<T extends Record<string, unknown>>(
  form: UseFormReturn<T>,
  error: ApiError,
  knownFields: readonly string[],
): boolean {
  const field = typeof error.details?.['field'] === 'string' ? error.details['field'] : undefined;
  if (error.is('CONFLICT') && field && knownFields.includes(field)) {
    form.setError(field as Path<T>, {
      message: t(`schedules.templates.form.errors.${field}Taken`),
    });
    return true;
  }
  if (error.is('PERIOD_CLOSED') && knownFields.includes('effective_from')) {
    form.setError('effective_from' as Path<T>, { message: scheduleErrorMessage(error) });
    return true;
  }
  const issues = error.details?.['issues'];
  if (error.is('VALIDATION_ERROR') && Array.isArray(issues)) {
    let applied = false;
    for (const issue of issues as { path?: unknown; message?: unknown }[]) {
      const path = typeof issue.path === 'string' ? issue.path.split('.')[0] : undefined;
      if (path && knownFields.includes(path) && typeof issue.message === 'string') {
        form.setError(path as Path<T>, { message: issue.message });
        applied = true;
      }
    }
    return applied;
  }
  return false;
}

/** Ayuda viva bajo el fin: duración y "Cruza la medianoche" cuando las horas son válidas. */
function durationHint(start: string, end: string): string {
  const base = t('schedules.templates.form.help.endTime');
  if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end) || start === end) {
    return base;
  }
  const minutes = shiftDurationMinutes(start, end);
  if (minutes < SHIFT_MIN_DURATION_MINUTES || minutes > SHIFT_MAX_DURATION_MINUTES) {
    return base;
  }
  const parts = [
    base,
    t('schedules.templates.form.help.duration', { duration: durationLabel(minutes) }),
  ];
  if (end <= start) {
    parts.push(t('schedules.templates.crossesMidnight'));
  }
  return parts.join(' · ');
}

/** Inicio, fin, refrigerio y tolerancias propias. `form` es cualquier formulario que los incluya. */
export function ShiftTimesFields<T extends ShiftTimesValues>({
  form,
  companyLateTolerance,
  companyEarlyTolerance,
}: {
  form: UseFormReturn<T>;
  companyLateTolerance: number | null;
  companyEarlyTolerance: number | null;
}) {
  // El genérico `T` impide que TS resuelva los nombres de campo: se trabaja sobre la vista común.
  const view = form as unknown as UseFormReturn<ShiftTimesValues>;
  const { register, formState, control } = view;
  const errors = formState.errors;
  const [start, end, own] = useWatch({
    control,
    name: ['start_time', 'end_time', 'own_tolerances'],
  });

  return (
    <>
      <div className="grid grid-cols-2 gap-fm-3">
        <Field
          label={t('schedules.templates.fields.startTime')}
          required
          error={errors.start_time?.message}
        >
          {(controlProps) => (
            <Input {...controlProps} type="time" step={60} {...register('start_time')} />
          )}
        </Field>
        <Field
          label={t('schedules.templates.fields.endTime')}
          required
          error={errors.end_time?.message}
        >
          {(controlProps) => (
            <Input {...controlProps} type="time" step={60} {...register('end_time')} />
          )}
        </Field>
      </div>
      <p className="-mt-fm-2 text-fm-xs text-fm-text-secondary" data-testid="shift-duration-hint">
        {durationHint(start, end)}
      </p>

      <Field
        label={t('schedules.templates.fields.breakMinutes')}
        help={t('schedules.templates.form.help.breakMinutes')}
        error={errors.break_minutes?.message}
      >
        {(controlProps) => (
          <Input
            {...controlProps}
            type="number"
            inputMode="numeric"
            min={0}
            max={180}
            step={1}
            {...register('break_minutes')}
          />
        )}
      </Field>

      <label className="flex min-h-fm-touch items-center gap-fm-2 text-fm-sm text-fm-text">
        <input
          type="checkbox"
          className="size-4 accent-[var(--fm-primary)]"
          {...register('own_tolerances')}
        />
        {t('schedules.templates.fields.ownTolerances')}
      </label>
      {own ? (
        <div className="grid grid-cols-2 gap-fm-3">
          <Field
            label={t('schedules.templates.fields.lateTolerance')}
            required
            error={errors.late_tolerance_minutes?.message}
          >
            {(controlProps) => (
              <Input
                {...controlProps}
                type="number"
                inputMode="numeric"
                min={0}
                max={60}
                step={1}
                {...register('late_tolerance_minutes')}
              />
            )}
          </Field>
          <Field
            label={t('schedules.templates.fields.earlyLeaveTolerance')}
            required
            error={errors.early_leave_tolerance_minutes?.message}
          >
            {(controlProps) => (
              <Input
                {...controlProps}
                type="number"
                inputMode="numeric"
                min={0}
                max={60}
                step={1}
                {...register('early_leave_tolerance_minutes')}
              />
            )}
          </Field>
        </div>
      ) : (
        <p className="text-fm-xs text-fm-text-secondary">
          {companyLateTolerance === null || companyEarlyTolerance === null
            ? t('schedules.templates.form.help.companyTolerancesUnknown')
            : t('schedules.templates.form.help.companyTolerances', {
                late: companyLateTolerance,
                early: companyEarlyTolerance,
              })}
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Diálogo de alta y de edición de nombre
// ---------------------------------------------------------------------------

interface TemplateFormValues extends ShiftTimesValues, Record<string, unknown> {
  name: string;
  code: string;
}

const CREATE_FIELDS = ['name', 'code', ...SHIFT_TIME_FIELDS] as const;
const RENAME_FIELDS = ['name', 'code'] as const;

function emptyValues(template?: ShiftTemplate): TemplateFormValues {
  return {
    name: template?.name ?? '',
    code: template?.code ?? '',
    ...shiftTimesValues(),
  };
}

export interface TemplateFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Plantilla a renombrar; ausente = alta. */
  template?: ShiftTemplate | undefined;
  companyLateTolerance: number | null;
  companyEarlyTolerance: number | null;
  /** Se llama con el id de la plantilla creada o editada. */
  onSaved?: (id: string) => void;
}

export function TemplateFormDialog({
  open,
  onOpenChange,
  template,
  companyLateTolerance,
  companyEarlyTolerance,
  onSaved,
}: TemplateFormDialogProps) {
  const { toast } = useToast();
  const { readOnly } = useReadOnly();
  const create = useCreateShiftTemplate();
  const update = useUpdateShiftTemplate();
  const isRename = template !== undefined;
  const fields: readonly string[] = isRename ? RENAME_FIELDS : CREATE_FIELDS;

  const form = useForm<TemplateFormValues>({ defaultValues: emptyValues(template) });
  const { register, handleSubmit, reset, formState } = form;
  const errors = formState.errors;
  const pending = create.isPending || update.isPending;

  useEffect(() => {
    if (open) {
      reset(emptyValues(template));
    }
  }, [open, template, reset]);

  function onApiError(error: ApiError): void {
    if (!applyApiFieldError(form, error, fields)) {
      toast({ title: scheduleErrorMessage(error), variant: 'error' });
    }
  }

  const onSubmit = handleSubmit((values) => {
    form.clearErrors();
    if (isRename) {
      const parsed = UpdateShiftTemplateBodySchema.safeParse({
        name: values.name,
        code: values.code,
      });
      if (!parsed.success) {
        applyIssues(form, parsed.error.issues, fields);
        return;
      }
      const body: UpdateShiftTemplateBody = {};
      if (parsed.data.name !== undefined && parsed.data.name !== template.name) {
        body.name = parsed.data.name;
      }
      if (parsed.data.code !== undefined && parsed.data.code !== template.code) {
        body.code = parsed.data.code;
      }
      if (Object.keys(body).length === 0) {
        onOpenChange(false);
        return;
      }
      update.mutate(
        { id: template.id, body },
        {
          onSuccess: (saved) => {
            toast({ title: t('schedules.templates.toast.renamed'), variant: 'success' });
            onOpenChange(false);
            onSaved?.(saved.id);
          },
          onError: onApiError,
        },
      );
      return;
    }

    const parsed = CreateShiftTemplateBodySchema.safeParse({
      name: values.name,
      code: values.code,
      ...shiftTimesFromValues(values),
    });
    if (!parsed.success) {
      applyIssues(form, parsed.error.issues, fields);
      return;
    }
    create.mutate(parsed.data, {
      onSuccess: (saved) => {
        toast({
          title: t('schedules.templates.toast.created', { name: saved.name }),
          variant: 'success',
        });
        onOpenChange(false);
        onSaved?.(saved.id);
      },
      onError: onApiError,
    });
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={
          isRename
            ? t('schedules.templates.form.renameTitle')
            : t('schedules.templates.form.createTitle')
        }
        description={
          isRename
            ? t('schedules.templates.form.renameDescription')
            : t('schedules.templates.form.createDescription')
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
              {t('common.buttons.cancel')}
            </Button>
            <Button
              type="submit"
              form="shift-template-form"
              loading={pending}
              loadingText={t('common.states.saving')}
              {...readOnlyProps(readOnly)}
            >
              {isRename ? t('common.buttons.save') : t('schedules.templates.form.submit')}
            </Button>
          </>
        }
      >
        <form
          id="shift-template-form"
          data-testid="shift-template-form"
          className="flex flex-col gap-fm-4"
          onSubmit={(event) => void onSubmit(event)}
          noValidate
        >
          <div className="grid grid-cols-[1fr_120px] gap-fm-3">
            <Field
              label={t('schedules.templates.fields.name')}
              required
              error={errors.name?.message}
            >
              {(controlProps) => (
                <Input {...controlProps} autoComplete="off" maxLength={60} {...register('name')} />
              )}
            </Field>
            <Field
              label={t('schedules.templates.fields.code')}
              required
              help={t('schedules.templates.form.help.code')}
              error={errors.code?.message}
            >
              {(controlProps) => (
                <Input
                  {...controlProps}
                  autoComplete="off"
                  maxLength={4}
                  className="uppercase"
                  {...register('code')}
                />
              )}
            </Field>
          </div>

          {isRename ? null : (
            <ShiftTimesFields
              form={form}
              companyLateTolerance={companyLateTolerance}
              companyEarlyTolerance={companyEarlyTolerance}
            />
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
